/**
 * Which tool has the floor, and how it gets and gives it back.
 *
 * An external store rather than component state because it is driven from
 * non-React paths - a click resolved in the 3D scene, the E key, a restored
 * session - and read from the frame loop. React's ToolPanelFrame is a
 * projection of it: this store says WHO is open, acquires the walk-lock and
 * starts the camera glide; the frame owns the async chunk load and the
 * mount/unmount lifecycle.
 *
 * Opening records the journal 'visit' here - once per open, not once per
 * language switch (the old rebuild-on-language path double-counted).
 */

import type { Tool } from '../../fab/layout'
import type { PanelServices } from '../../tools/types'
import { acquireWalkLock } from '../input/walkLock'

export type { PanelServices }

let open: Tool | null = null
let services: PanelServices | null = null
let glideTo: ((tool: Tool) => void) | null = null
/** Released when the open panel closes; null while the student has the floor. */
let walkLock: (() => void) | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

export const panelStore = {
  get open(): Tool | null {
    return open
  },

  /** The host services, for the frame to build each panel's ToolPanelHost. */
  get services(): PanelServices | null {
    return services
  },

  /** Wired once by the composition root, when the world exists. */
  init(panelServices: PanelServices, glide: (tool: Tool) => void): void {
    services = panelServices
    glideTo = glide
  },

  openTool(tool: Tool): void {
    if (!services || open === tool) return
    // Evidence, not analytics: the work-order evaluator matches on this, so
    // it carries the stable layout id rather than a translated name.
    services.journal.record({ kind: 'visit', tool: tool.id })
    open = tool
    walkLock ??= acquireWalkLock()
    // Move the eye to the HMI so closing the panel leaves the student
    // standing where the tool is, not wherever they happened to be facing.
    glideTo?.(tool)
    notify()
  },

  closeTool(): void {
    if (!open) return
    open = null
    walkLock?.()
    walkLock = null
    notify()
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
